import { useCallback, useContext, useEffect } from 'react';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { useSearchParams } from 'react-router-dom';

import { WithLocalStorage } from '@/components';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { PrimaryContext } from '@/services';
import {
  useFertilizationsBySemenId,
  useSemen,
  useSemenTransactionsV2,
} from '@/services/semen-service';
import { useContextMenu } from '@/utils/context-menu';

import { useSemenAppContext } from '../../context/SemenAppContext';
import {
  applySemenTypeLabelToColumnDefs,
  fertilizationsColumnDefs,
  semenTransactionColumnDefs,
} from '../../util/column-defs';
import { SemenActions } from '../HomeTab/SemenActions';
import { AdditionalInformation } from './AdditionalInformation';
import { BatchInformation } from './BatchInformation';

export const BatchDetailTab = () => {
  const defaultGridOptions = useDefaultGridOptions();
  const isSemenTypeLabelEnabled = useFeatureFlag('Semen_Type_Field_Label');
  const isSemenConventionalTypeEnabled = useFeatureFlag(
    'Semen_Conventional_Type',
  );
  const isFertilizationDataErrorEnabled = useFeatureFlag(
    'Fertilization_Data_Error',
  );
  const filteredSemenTransactionColumnDefs = applySemenTypeLabelToColumnDefs(
    isSemenTypeLabelEnabled
      ? semenTransactionColumnDefs.filter((col: any) => col.field !== 'sexed')
      : semenTransactionColumnDefs,
    isSemenTypeLabelEnabled,
    isSemenConventionalTypeEnabled,
  );
  const { themeMode } = useContext(PrimaryContext);
  const { getContextMenuItems: fertilizationsContextMenu } = useContextMenu({
    idKey: 'fertilizationId',
  });
  const { getContextMenuItems: transactionsContextMenu } = useContextMenu({
    idKey: 'id',
  });

  const [searchParams] = useSearchParams();
  const semenId = searchParams.get('semenId');

  const { selectedSemen, setSelectedSemen } = useSemenAppContext();

  const {
    data: semenData,
    isLoading: semenLoading,
    refetch: refetchSemen,
  } = useSemen(semenId ? { semenId } : {});

  const semen = semenData ? semenData?.items?.[0] : undefined;

  const {
    data: fertilizations,
    isLoading: fertilizationsLoading,
    error: fertilizationsError,
    refetch: refetchFertilizations,
  } = useFertilizationsBySemenId(semenId || '');

  const {
    data: transactionsV2,
    isLoading: transactionsLoadingV2,
    refetch: refetchTransactionsV2,
  } = useSemenTransactionsV2(semenId ? { semenId } : {});

  // Set the loaded semen into the context so it's available for the AddTransactionDialog
  useEffect(() => {
    if (semen) {
      setSelectedSemen([semen]);
    }
  }, [semen, setSelectedSemen]);

  // Callback to refresh data after changes (e.g., after adding a transaction)
  const handleFilterChanged = useCallback(() => {
    refetchSemen();
    refetchTransactionsV2();
    refetchFertilizations();
  }, [refetchSemen, refetchTransactionsV2, refetchFertilizations]);

  if (semenLoading) {
    return (
      <div className="flex h-64 flex-col items-center justify-center">
        <p className="text-gray-500">Loading semen details...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex justify-between">
        <div className="flex flex-wrap gap-2">
          <SemenActions
            gridRef={{ current: null }}
            onFilterChanged={handleFilterChanged}
            reportSemenIds={semen ? [semen.id] : []}
          />
        </div>
      </div>

      <BatchInformation semen={semen} />

      <Accordion>
        <AccordionTab header="Semen Transactions">
          <div className="h-[30rem]">
            {transactionsLoadingV2 && (
              <p className="text-sm text-gray-500">Loading transactions...</p>
            )}
            {transactionsV2 &&
              transactionsV2.items.length === 0 &&
              !transactionsLoadingV2 && (
                <p className="text-sm text-gray-500">
                  No transactions found for this semen batch.
                </p>
              )}
            {transactionsV2 && transactionsV2.items.length > 0 && (
              <WithLocalStorage storageKey="batch-detail-transactions-grid">
                <AgGridReact
                  gridOptions={defaultGridOptions}
                  className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} ${'batch-detail'}`.trim()}
                  rowData={transactionsV2.items || []}
                  columnDefs={filteredSemenTransactionColumnDefs}
                  rowSelection="single"
                  suppressRowClickSelection={false}
                  defaultColDef={defaultColDef}
                  getContextMenuItems={transactionsContextMenu}
                />
              </WithLocalStorage>
            )}
          </div>
        </AccordionTab>

        <AccordionTab header="Fertilizations">
          <div className="h-[30rem]">
            {fertilizationsLoading && (
              <p className="text-sm text-gray-500">Loading fertilizations...</p>
            )}
            {fertilizationsError &&
              (isFertilizationDataErrorEnabled ? (
                <p className="text-sm text-gray-500">
                  No fertilizations found for this semen batch.
                </p>
              ) : (
                <p className="text-sm text-red-500">
                  Error loading fertilizations: {fertilizationsError.message}
                </p>
              ))}
            {fertilizations &&
              fertilizations.length === 0 &&
              !fertilizationsLoading && (
                <p className="text-sm text-gray-500">
                  No fertilizations found for this semen batch.
                </p>
              )}
            {fertilizations && fertilizations.length > 0 && (
              <WithLocalStorage storageKey="batch-detail-fertilizations-grid">
                <AgGridReact
                  gridOptions={defaultGridOptions}
                  className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} ${'batch-detail'}`.trim()}
                  rowData={fertilizations || []}
                  columnDefs={fertilizationsColumnDefs}
                  rowSelection="single"
                  suppressRowClickSelection={false}
                  defaultColDef={defaultColDef}
                  getContextMenuItems={fertilizationsContextMenu}
                />
              </WithLocalStorage>
            )}
          </div>
        </AccordionTab>

        <AccordionTab header="Additional Info">
          <AdditionalInformation semen={semen} />
        </AccordionTab>
      </Accordion>
    </div>
  );
};
